import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import AdminStatCard from '../../components/admin/AdminStatCard';
import { getDashboardSummary } from '../../services/AdminDashboardService';
import { formatDate } from '../../utils/dateUtils';

const DashboardPage = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const data = await getDashboardSummary();
        setDashboardData(data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="alert alert-danger">{error}</div>
      </AdminLayout>
    );
  }

  // If data is not available yet, show placeholder
  if (!dashboardData || !dashboardData.stats) {
    return (
      <AdminLayout>
        <div className="alert alert-warning">No dashboard data available</div>
      </AdminLayout>
    );
  }

  const { stats, latestUsers, latestEvents, recentCheckins, recentMoments } = dashboardData;

  return (
    <AdminLayout>
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h1 className="h3 mb-0">Dashboard</h1>
          <p className="text-muted">Welcome to the SPONO Admin Dashboard</p>
        </div>
        <div>
          <button className="btn btn-primary me-2">
            <i className="fa fa-download me-2"></i> Export Report
          </button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <AdminStatCard
            title="Total Users"
            value={stats.totalUsers}
            icon="fa-users"
            change={5.2}
            changeType="increase"
            linkTo="/admin/users"
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-4">
          <AdminStatCard
            title="Total Events"
            value={stats.totalEvents}
            icon="fa-calendar"
            iconBg="var(--dark-success)"
            change={2.4}
            changeType="increase"
            linkTo="/admin/events"
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-4">
          <AdminStatCard
            title="Total Check-ins"
            value={stats.totalCheckins}
            icon="fa-check-circle"
            iconBg="var(--dark-info)"
            change={1.8}
            changeType="decrease"
            linkTo="/admin/check-ins"
          />
        </div>
        <div className="col-xl-3 col-md-6 mb-4">
          <AdminStatCard
            title="Total Moments"
            value={stats.totalFootages}
            icon="fa-image"
            iconBg="var(--dark-warning)"
            change={7.5}
            changeType="increase"
            linkTo="/admin/moments"
          />
        </div>
      </div>
      
      {/* Latest Users and Events */}
      <div className="row">
        <div className="col-md-6 mb-4">
          <AdminCard
            title="Latest Users"
            headerActions={
              <Link to="/admin/users" className="btn btn-sm btn-outline-primary">
                View All
              </Link>
            }
            noPadding
          >
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--dark-bg-tertiary)' }}>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {latestUsers?.length > 0 ? (
                    latestUsers.map(user => (
                      <tr key={user.id} style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                        <td>
                          <Link to={`/admin/users/${user.id}`}>
                            {user.name}
                          </Link>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`badge bg-${user.user_type === 'event_promoter' ? 'success' : 'info'}`}>
                            {user.user_type}
                          </span>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                      <td colSpan="4" className="text-center">No users found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </AdminCard>
        </div>
        
        <div className="col-md-6 mb-4">
          <AdminCard
            title="Latest Events"
            headerActions={
              <Link to="/admin/events" className="btn btn-sm btn-outline-primary">
                View All
              </Link>
            }
            noPadding
          >
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--dark-bg-tertiary)' }}>
                    <th>Title</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {latestEvents?.length > 0 ? (
                    latestEvents.map(event => (
                      <tr key={event.id} style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                        <td>
                          <Link to={`/admin/events/${event.id}`}>
                            {event.event_title}
                          </Link>
                        </td>
                        <td>{formatDate(event.event_date)}</td>
                        <td>
                          <span className={`badge bg-${event.status === 'active' ? 'success' : 'warning'}`}>
                            {event.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                      <td colSpan="3" className="text-center">No events found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </AdminCard>
        </div>
      </div>
      
      {/* Recent Check-ins and Moments */}
      <div className="row">
        <div className="col-md-6 mb-4">
          <AdminCard
            title="Recent Check-ins"
            headerActions={
              <Link to="/admin/check-ins" className="btn btn-sm btn-outline-primary">
                View All
              </Link>
            }
            noPadding
          >
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--dark-bg-tertiary)' }}>
                    <th>User</th>
                    <th>Event</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCheckins?.length > 0 ? (
                    recentCheckins.map(checkin => (
                      <tr key={checkin.id} style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                        <td>
                          <Link to={`/admin/users/${checkin.user_id}`}>
                            {checkin.user_name || 'Unknown user'}
                          </Link>
                        </td>
                        <td>
                          <Link to={`/admin/events/${checkin.event_id}`}>
                            {checkin.event_title || 'Unknown event'}
                          </Link>
                        </td>
                        <td>{formatDate(checkin.checkin_date)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                      <td colSpan="3" className="text-center">No recent check-ins</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </AdminCard>
        </div>
        
        <div className="col-md-6 mb-4">
          <AdminCard
            title="Recent Moments"
            headerActions={
              <Link to="/admin/moments" className="btn btn-sm btn-outline-primary">
                View All
              </Link>
            }
            noPadding
          >
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--dark-bg-tertiary)' }}>
                    <th>Event</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMoments?.length > 0 ? (
                    recentMoments.map(moment => (
                      <tr key={moment.id} style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                        <td>
                          <Link to={`/admin/events/${moment.event_id}`}>
                            {moment.event_title || 'Unknown event'}
                          </Link>
                        </td>
                        <td>{formatDate(moment.uploaded_at)}</td>
                        <td>
                          <a 
                            href={moment.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-secondary"
                          >
                            <i className="fa fa-eye"></i> View
                          </a>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ backgroundColor: 'var(--dark-bg-secondary)' }}>
                      <td colSpan="3" className="text-center">No recent moments</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </AdminCard>
        </div>
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
